import { array } from 'fp-ts';
import { useMemo } from 'react';
import { Banner, Link, Stack, StackItem, Typography } from '@optimus-web/core';
import { UnicodeEntity } from '@mews/enums';
import { LocalizedText } from '@mews/framework';
import { TextKey, ArticlePath, createGuideLink } from '@mews-commander/enums';
import { LocalizedTypography } from '@mews-commander/ui';

interface Props {
    numberOfDigits: number;
    prefix: string | null;
    suffix: string | null;
    startValue: number;
}

export const CounterFormatPreviewBanner = ({ numberOfDigits, suffix, prefix, startValue }: Props) => {
    const numberPreview = useMemo(() => {
        const useStartValue = startValue.toString().length > numberOfDigits;
        const numberOfZeros = numberOfDigits - startValue.toString().length;

        const zeros = array.makeBy(numberOfZeros, (_) => 0).join('');
        const properValue = `${zeros}${startValue}`;

        return `${prefix || ''}${useStartValue ? startValue : properValue}${suffix || ''}`;
    }, [startValue, numberOfDigits, prefix, suffix]);

    return (
        <Banner
            primaryText={
                <Stack vertical spacing="300">
                    <StackItem>
                        <LocalizedTypography
                            as="span"
                            textStyle="bodyMedium"
                            textKey={TextKey.CreditNoteCounterFormEducationText}
                        />
                        {UnicodeEntity.Space}
                        <Link
                            underline={false}
                            target="_blank"
                            href={createGuideLink(ArticlePath.CreditNoteCounters)}
                            rel="noreferrer"
                        >
                            <LocalizedTypography
                                as="span"
                                underline
                                textStyle="bodyMedium"
                                textKey={TextKey.LearnMore}
                            />
                        </Link>
                    </StackItem>
                    <StackItem>
                        {prefix ? (
                            <>
                                <Typography as="span" textStyle="bodyLargeStrong">
                                    {prefix}
                                    {UnicodeEntity.Space}
                                </Typography>
                                <Typography textStyle="bodyMedium" as="span">
                                    {UnicodeEntity.LeftParenthesis}
                                    <LocalizedText textKey={TextKey.CreditNoteCounterPrefixTitle} />
                                    {UnicodeEntity.RightParenthesis}
                                    {UnicodeEntity.Space}
                                </Typography>
                            </>
                        ) : null}
                        <Typography as="span" textStyle="bodyLargeStrong">
                            {array.makeBy(numberOfDigits || 1, (_) => 0).join('')}
                        </Typography>
                        {UnicodeEntity.Space}
                        <Typography textStyle="bodyMedium" as="span">
                            {UnicodeEntity.LeftParenthesis}
                            <LocalizedText textKey={TextKey.CreditNoteCounterNumberOfDigitsLabel} />
                            {UnicodeEntity.RightParenthesis}
                            {UnicodeEntity.Space}
                            {UnicodeEntity.PlusSign}
                            {UnicodeEntity.Space}
                            {startValue}
                            {UnicodeEntity.Space}
                            {UnicodeEntity.LeftParenthesis}
                            <LocalizedText textKey={TextKey.StartValue} />
                            {UnicodeEntity.RightParenthesis}
                        </Typography>

                        {suffix ? (
                            <>
                                {UnicodeEntity.Space}
                                <Typography as="span" textStyle="bodyLargeStrong">
                                    {suffix}
                                    {UnicodeEntity.Space}
                                </Typography>
                                <Typography textStyle="bodyMedium" as="span">
                                    {UnicodeEntity.LeftParenthesis}
                                    <LocalizedText textKey={TextKey.CreditNoteCounterSuffixTitle} />
                                    {UnicodeEntity.RightParenthesis}
                                </Typography>
                            </>
                        ) : null}
                        {UnicodeEntity.Space}
                        <span>{UnicodeEntity.Equals}</span>
                        {UnicodeEntity.Space}
                        <Typography as="span" textStyle="bodyLargeStrong">
                            {numberPreview}
                        </Typography>
                    </StackItem>
                </Stack>
            }
            variant="info"
        />
    );
};
